import { Injectable, NgZone, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Manages the authenticated session lifecycle:
 *
 *  - Idle detection: after {@link IDLE_LIMIT_MS} of no user activity a warning
 *    dialog is shown with a short countdown; if the user doesn't respond the
 *    session is ended and they are returned to /login.
 *  - Proactive token refresh: while the user is active the access token is
 *    silently refreshed shortly before it expires, so a working session never
 *    breaks on the 1-hour token expiry (the interceptor still covers 401s that
 *    slip through reactively).
 *
 * Starts/stops automatically with the authentication state.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private auth   = inject(AuthService);
  private router = inject(Router);
  private zone   = inject(NgZone);

  /** Idle time before the warning appears. */
  readonly IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
  /** Grace period to respond to the warning before auto sign-out. */
  readonly WARNING_MS = 2 * 60 * 1000; // 2 minutes
  /** Refresh this long before the token actually expires. */
  private readonly REFRESH_SKEW_MS = 60 * 1000; // 60 seconds
  /** Fallback token lifetime when the expiry can't be read from the token. */
  private readonly DEFAULT_TTL_MS = 60 * 60 * 1000; // 1 hour

  /** Whether the idle-warning dialog is currently shown. */
  readonly warningVisible = signal(false);
  /** Seconds remaining before auto sign-out while the warning is shown. */
  readonly countdown = signal(0);
  readonly countdownLabel = computed(() => {
    const s = Math.max(this.countdown(), 0);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  });

  private started = false;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private countdownTimer?: ReturnType<typeof setInterval>;

  private readonly activityEvents =
    ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;
  private readonly onActivity = () => this.handleActivity();

  constructor() {
    // Follow the auth state: monitor while signed in, tear down when signed out.
    effect(() => {
      if (this.auth.isAuthenticated()) this.start();
      else this.stop();
    });
  }

  private start(): void {
    if (this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach(e =>
        window.addEventListener(e, this.onActivity, { passive: true }));
    });
    this.resetIdle();
    this.scheduleRefresh();
  }

  private stop(): void {
    if (!this.started) return;
    this.started = false;
    this.activityEvents.forEach(e => window.removeEventListener(e, this.onActivity));
    this.clearTimers();
    this.warningVisible.set(false);
  }

  private clearTimers(): void {
    clearTimeout(this.idleTimer);
    clearTimeout(this.refreshTimer);
    clearInterval(this.countdownTimer);
  }

  private handleActivity(): void {
    // While the warning is up the user must make an explicit choice; ignore
    // background activity so the countdown isn't silently cancelled.
    if (this.warningVisible()) return;
    this.resetIdle();
  }

  private resetIdle(): void {
    clearTimeout(this.idleTimer);
    this.zone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(
        () => this.zone.run(() => this.showWarning()),
        this.IDLE_LIMIT_MS,
      );
    });
  }

  private showWarning(): void {
    this.warningVisible.set(true);
    this.countdown.set(Math.round(this.WARNING_MS / 1000));
    this.countdownTimer = setInterval(() => {
      const next = this.countdown() - 1;
      this.countdown.set(next);
      if (next <= 0) {
        clearInterval(this.countdownTimer);
        this.forceLogout();
      }
    }, 1000);
  }

  /** User chose to stay signed in: refresh the token and resume monitoring. */
  continueSession(): void {
    clearInterval(this.countdownTimer);
    this.warningVisible.set(false);
    this.auth.refreshToken().subscribe({
      next: token => {
        if (!token) { this.forceLogout(); return; }
        this.resetIdle();
        this.scheduleRefresh();
      },
      error: () => this.forceLogout(),
    });
  }

  /** User chose to sign out from the warning dialog. */
  logoutNow(): void {
    this.forceLogout();
  }

  private forceLogout(): void {
    this.stop();
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  /** Schedule a silent refresh shortly before the current token expires. */
  private scheduleRefresh(): void {
    clearTimeout(this.refreshTimer);
    const expiry = this.tokenExpiryMs();
    const delay = expiry
      ? Math.max(expiry - Date.now() - this.REFRESH_SKEW_MS, 5_000)
      : this.DEFAULT_TTL_MS - this.REFRESH_SKEW_MS;
    this.zone.runOutsideAngular(() => {
      this.refreshTimer = setTimeout(
        () => this.zone.run(() => this.proactiveRefresh()),
        delay,
      );
    });
  }

  private proactiveRefresh(): void {
    // Don't silently keep an idle session alive — the warning dialog governs that.
    if (this.warningVisible()) return;
    this.auth.refreshToken().subscribe({
      next: token => {
        if (token) this.scheduleRefresh();
        else this.forceLogout();
      },
      // On a transient error leave it to the interceptor's reactive 401 refresh.
      error: () => {},
    });
  }

  /** Reads the JWT `exp` claim (ms epoch), or null when unavailable. */
  private tokenExpiryMs(): number | null {
    const token = this.auth.getToken();
    const part = token?.split('.')[1];
    if (!part) return null;
    try {
      const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json) as { exp?: number };
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
